import { Card } from "@/components/ui/card";
import type { NoiseProgressMap } from "@/hooks/useRunStream";
import type { NoiseDeliveryStats, RunTemplateBinding } from "@/api/types";

function formatTime(iso: string): string {
  return new Date(iso).toISOString().substring(11, 19);
}

function DeliveryBadge({ stats }: { stats: NoiseDeliveryStats | null | undefined }) {
  if (!stats) return null;
  const total = stats.sent + stats.failed;
  if (stats.failed === 0) {
    return (
      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-border bg-muted/40 text-muted-foreground">
        delivery: {stats.sent}/{total} ({Math.round(stats.window_seconds / 60)}m)
      </span>
    );
  }
  return (
    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-red-500/40 bg-red-500/[0.08] text-red-700 dark:text-red-300">
      delivery: {stats.sent}/{total} · last {stats.last_error_kind}{" "}
      {stats.last_error_at ? `at ${formatTime(stats.last_error_at)}` : ""} ({Math.round(stats.window_seconds / 60)}m)
    </span>
  );
}

export function NoiseStreamPanel({
  bindings,
  progress,
  noiseDeliveryStats,
}: {
  bindings: RunTemplateBinding[];
  progress: NoiseProgressMap;
  noiseDeliveryStats?: NoiseDeliveryStats | null;
}) {
  if (bindings.length === 0) {
    return (
      <Card className="p-4 text-sm text-muted-foreground">
        No noise streams bound to this run.
      </Card>
    );
  }
  return (
    <Card className="p-4 space-y-2">
      <div className="flex items-center gap-2">
        <h3 className="font-medium">Noise streams</h3>
        <DeliveryBadge stats={noiseDeliveryStats ?? null} />
      </div>
      <div className="space-y-1">
        {bindings.map((b) => {
          const p = progress[b.template_id] ?? { sent: 0, failed: 0 };
          return (
            <div key={b.template_id} className="flex items-center gap-2 text-sm">
              <span className="font-mono w-48">{b.template_id}</span>
              <span className="text-muted-foreground">
                {b.rate_per_minute ?? "default"}/min
              </span>
              <span className="ml-auto">
                <span className="text-green-600 dark:text-green-300">sent {p.sent}</span>
                {p.failed > 0 && (
                  <span className="ml-2 text-red-600 dark:text-red-300">
                    failed {p.failed}
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
